import { useEffect } from "react";
import { Navigate, Route, Routes, useLocation, useNavigate, useParams } from "react-router-dom";
import { RequireSession } from "./components/RequireSession.tsx";
import { Shell } from "./components/Shell.tsx";
import { TitleCycler } from "./components/TitleCycler.tsx";
import { CONSOLE_ORIGIN, PROJECT_PATH_PREFIX, onConsoleHost, projectPath } from "./lib/hosts.ts";
import { AuthAdopt } from "./routes/AuthAdopt.tsx";
import { AuthReset } from "./routes/AuthReset.tsx";
import { ConnectApprove } from "./routes/ConnectApprove.tsx";
import { DocPage, DocsIndex } from "./routes/Docs.tsx";
import { Home } from "./routes/Home.tsx";
import { NotFound } from "./routes/NotFound.tsx";
import { Pricing } from "./routes/Pricing.tsx";
import { Security } from "./routes/Security.tsx";
import { SignIn } from "./routes/SignIn.tsx";
import { AdminHome } from "./routes/admin/AdminHome.tsx";
import { Fleet } from "./routes/admin/Fleet.tsx";
import { Observability } from "./routes/admin/Observability.tsx";
import { AgentAccess } from "./routes/console/AgentAccess.tsx";
import { ConsoleHome } from "./routes/console/ConsoleHome.tsx";
import { ConsoleLayout } from "./routes/console/ConsoleLayout.tsx";
import { ProjectConsole } from "./routes/console/ProjectConsole.tsx";
import { CreateApp } from "./routes/customer/CreateApp.tsx";
import { UpgradeRoute } from "./routes/customer/Upgrade.tsx";
import { takeReturnTo, useSession } from "./session/SessionProvider.tsx";

// Normalizes trailing slashes and performs the post-login returnTo redirect
// once the session is ready.
function RouteHelper() {
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const { ready, signedIn } = useSession();

  useEffect(() => {
    if (pathname !== "/" && pathname.endsWith("/")) {
      navigate(pathname.replace(/\/+$/, "") + search, { replace: true });
    }
  }, [pathname, search, navigate]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: fire once when the session becomes ready; takeReturnTo is idempotent so re-runs are harmless.
  useEffect(() => {
    if (!ready || !signedIn) return;
    const rt = takeReturnTo();
    if (rt && rt !== pathname) navigate(rt, { replace: true });
  }, [ready]);

  return null;
}

/** Anything console-shaped that still arrives at the apex goes to the console
 *  HOST. There is one place to manage apps, and it isn't the marketing site. */
function ToConsoleHost() {
  const { slug } = useParams();
  useEffect(() => {
    // With a slug this is a PROJECT link (`/console/{slug}`), so it has to land
    // on the prefixed path; without one it's the app list, which is `/`.
    window.location.replace(`${CONSOLE_ORIGIN}${slug ? projectPath(slug) : "/"}`);
  }, [slug]);
  return null;
}

/** THE SESSION LIVES ON ONE ORIGIN. Both hosts serve this bundle, but browser
 *  sessions are `localStorage`, which is partitioned per origin — so a sign-in
 *  completed on the apex is invisible to `console.s3p.dev`, and every path that
 *  ends at the console made the user sign in a SECOND time. It also meant
 *  `s3p connect` printed a link to a tab that was, by construction, signed out:
 *  approving a machine asked for a fresh email code from someone already signed
 *  in on the console next door.
 *
 *  So the apex serves no session-bearing page. It forwards them, PATH AND
 *  FRAGMENT INTACT — `/auth/connect#{deviceId}` and `/auth/adopt#code=…` carry
 *  their whole payload in the hash, and dropping it would strand the user on a
 *  page that can only say "this link is missing its device id".
 *
 *  `/auth/reset` deliberately stays on BOTH: it's stateless (everything it
 *  needs is in the fragment), and its apex URL is already baked into sent
 *  password-reset emails. */
function ToConsole() {
  const { pathname, search, hash } = useLocation();
  useEffect(() => {
    window.location.replace(`${CONSOLE_ORIGIN}${pathname}${search}${hash}`);
  }, [pathname, search, hash]);
  return null;
}

/** A project's console, deep link preserved: returnTo is the FULL current path
 *  (/p/{slug}/{panel}), so signing in lands you on the page you asked for, not
 *  the app list. */
function ProjectConsoleRoute() {
  const { pathname } = useLocation();
  return (
    <RequireSession returnTo={pathname}>
      <ConsoleLayout>
        <ProjectConsole />
      </ConsoleLayout>
    </RequireSession>
  );
}

/** `/{slug}/…` → `/p/{slug}/…`, in-app and preserving everything after the slug.
 *
 *  A `<Navigate replace>` rather than a rewrite: the old path should not stay in
 *  the address bar, or every link copied out of the console re-seeds the shape
 *  we just moved away from. */
function LegacyProjectRedirect() {
  const { slug = "", "*": rest = "" } = useParams();
  const { search, hash } = useLocation();
  return <Navigate to={`${projectPath(slug, rest)}${search}${hash}`} replace />;
}

/** The upgrade page lives on the console host (it needs the console session);
 *  honour marketing-host links to it. */
function ToConsoleUpgrade() {
  const { slug } = useParams();
  useEffect(() => {
    window.location.replace(`${CONSOLE_ORIGIN}/upgrade/${slug ?? ""}`);
  }, [slug]);
  return null;
}

/** TWO HOSTS, TWO NAVIGATION MODELS — and no overlap between them.
 *
 *  console.s3p.dev  the product.   `/` = all apps, `/p/{slug}` = that app's console,
 *                                  `/admin` = the operator surface.
 *  s3p.dev          the marketing. Landing, docs, pricing, sign-up.
 *
 *  Registering BOTH sets on both hosts (which is what the first cut did) means the
 *  static `/console` route out-ranks `/:slug` on the console host, so
 *  `console.s3p.dev/console` silently rendered the app LIST instead of the
 *  console project's own console. Each host now serves exactly one model, and the
 *  other host's paths redirect rather than resolving to something plausible-but-wrong.
 */
export function App() {
  const consoleHost = onConsoleHost();

  return (
    <>
      <RouteHelper />
      <Shell>
        {consoleHost ? (
          <Routes>
            {/* the product */}
            <Route
              path="/"
              element={
                <RequireSession returnTo="/">
                  <ConsoleLayout>
                    <ConsoleHome />
                  </ConsoleLayout>
                </RequireSession>
              }
            />
            <Route
              path="/admin"
              element={
                <RequireSession admin returnTo="/admin">
                  <AdminHome />
                </RequireSession>
              }
            />
            <Route
              path="/admin/observability"
              element={
                <RequireSession admin returnTo="/admin/observability">
                  <Observability />
                </RequireSession>
              }
            />
            <Route
              path="/admin/fleet"
              element={
                <RequireSession admin returnTo="/admin/fleet">
                  <Fleet />
                </RequireSession>
              }
            />
            {/* The agent hand-off, off the home page and onto its own route.
                A static segment out-ranks `/:slug/*` below, so a project that
                happened to be called `agent` is unaffected — its console is
                `/p/agent`, like every other project's. */}
            <Route
              path="/agent"
              element={
                <RequireSession returnTo="/agent">
                  <ConsoleLayout>
                    <AgentAccess />
                  </ConsoleLayout>
                </RequireSession>
              }
            />
            <Route path="/signin" element={<SignIn />} />
            <Route path="/auth/adopt" element={<AuthAdopt />} />
            <Route path="/auth/connect" element={<ConnectApprove />} />
            <Route path="/auth/reset" element={<AuthReset />} />
            <Route path="/signup" element={<CreateApp />} />
            {/* OLD signup paths, kept as redirects — links to /new and /app/new
                are already out in the world (sign-in page, emails, bookmarks).
                Must come BEFORE /:slug, or the first segment reads as a slug. */}
            <Route path="/new" element={<Navigate to="/signup" replace />} />
            <Route path="/app/new" element={<Navigate to="/signup" replace />} />
            {/* `/app` WAS the app list, and it's still what docs, emails, the
                agent skill and half this codebase link to. On this host it has
                no route of its own, so it fell through to `/:slug/*` and opened
                the console of a project called "app" — a plausible-looking page
                about nothing. It's the app list; say so. (After /app/new, or
                that would read as slug "new".) */}
            <Route path="/app" element={<Navigate to="/" replace />} />
            <Route path="/app/*" element={<Navigate to="/" replace />} />
            {/* Premium purchase (billing-checkout). BEFORE /:slug, or "upgrade"
                is read as a project. */}
            <Route path="/upgrade/:slug" element={<UpgradeRoute />} />
            {/* Projects live under their OWN segment (PROJECT_PATH_PREFIX), so a
                slug can never shadow a console page and a new console page can
                never steal a slug. A route per console panel lives below it:
                /p/{slug}/database, /p/{slug}/logs, … */}
            <Route path={`${PROJECT_PATH_PREFIX}/:slug/*`} element={<ProjectConsoleRoute />} />
            {/* LEGACY, and it has to stay: `console.s3p.dev/{slug}` is in the
                CLI's output, the edge's /server redirect, docs, the agent skill,
                sent emails and every bookmark. Redirect (deep path, query and
                hash intact) rather than resolving — this is LAST, so any real
                console route above still wins, exactly as before. */}
            <Route path="/:slug/*" element={<LegacyProjectRedirect />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        ) : (
          <>
            <TitleCycler />
            <Routes>
              {/* the marketing site */}
              <Route path="/" element={<Home />} />
              <Route path="/security" element={<Security />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/docs" element={<DocsIndex />} />
              <Route path="/docs/:slug" element={<DocPage />} />
              {/* EVERY session-bearing path → the console HOST (see ToConsole).
                  Signing in, signing up and both auth hand-offs establish a
                  session, and a session on this origin is a session the console
                  can't see. `/auth/reset` is the one exception: stateless, and
                  its apex URL is in emails already sent. */}
              <Route path="/agent" element={<ToConsole />} />
              <Route path="/signin" element={<ToConsole />} />
              <Route path="/auth/adopt" element={<ToConsole />} />
              <Route path="/auth/connect" element={<ToConsole />} />
              <Route path="/auth/reset" element={<AuthReset />} />
              <Route path="/signup" element={<ToConsole />} />
              <Route path="/app/new" element={<Navigate to="/signup" replace />} />
              <Route path="/new" element={<Navigate to="/signup" replace />} />

              {/* console-shaped paths → the console host */}
              <Route path="/console" element={<ToConsoleHost />} />
              <Route path="/console/:slug" element={<ToConsoleHost />} />
              <Route path="/app" element={<ToConsoleHost />} />
              <Route path="/app/:slug/console" element={<ToConsoleHost />} />
              <Route path="/upgrade/:slug" element={<ToConsoleUpgrade />} />
              <Route path="/admin" element={<ToConsoleHost />} />

              <Route path="*" element={<NotFound />} />
            </Routes>
          </>
        )}
      </Shell>
    </>
  );
}
